/**
 * Turns a failed deck load into something a person can act on.
 *
 * Before this existed, a file that would not open produced one of two things:
 * a `window.alert` on the landing screen, or a toast that vanished after three
 * seconds — and in the worst case the broken file was handed to the editor
 * anyway, which then rendered an empty deck. An empty deck is the single most
 * confusing outcome available: it looks like the file loaded and lost its
 * contents.
 *
 * So every open path now ends here, and the result is rendered as a proper
 * failure screen: what went wrong, where, and what to do about it.
 */

import { OpenPresentationErrorType } from './openPresentation';

/** Coarse classification, mostly so the UI can pick an icon and a heading. */
export const LoadErrorKind = {
  INVALID_JSON: 'invalid-json',
  EMPTY: 'empty',
  INVALID_FORMAT: 'invalid-format',
  UNREADABLE: 'unreadable',
  INCOMPLETE: 'incomplete',
  UNKNOWN: 'unknown'
};

const GENERIC_SUGGESTIONS = [
  'Open the file in a text editor and check it starts with { and ends with }.',
  'If an assistant wrote it, copy the reply again — replies are often cut off mid-file.',
  'Try the last version you saved from this app.'
];

/**
 * Describe a load failure.
 *
 * @param {unknown} error - An OpenPresentationError, a SyntaxError, or anything
 *   else that came out of a `catch`.
 * @param {{ fileName?: string, source?: 'file' | 'paste' | 'import' }} [context]
 * @returns {{
 *   kind: string,
 *   title: string,
 *   summary: string,
 *   fileName: string,
 *   location: { line: number, column: number, excerpt: string } | null,
 *   issues: string[],
 *   detail: string,
 *   suggestions: string[]
 * }}
 */
export function describeLoadError(error, context = {}) {
  const details = (error && error.details) || {};
  const fileName = context.fileName || details.fileName || '';
  const source = context.source || 'file';
  const subject = source === 'paste' ? 'The pasted text' : 'This file';

  const base = {
    kind: LoadErrorKind.UNKNOWN,
    title: 'This deck could not be opened',
    summary: `${subject} could not be opened, so your current work has been left exactly as it was.`,
    fileName,
    location: null,
    issues: [],
    detail: safeMessage(error),
    suggestions: GENERIC_SUGGESTIONS
  };

  if (details.empty) {
    return {
      ...base,
      kind: LoadErrorKind.EMPTY,
      title: 'That file is empty',
      summary: `${subject} contains no text at all — nothing was saved into it.`,
      suggestions: [
        'Check whether the save was interrupted, or the file was copied while still being written.',
        'Look for an earlier version of the deck alongside it.',
        'This app keeps a recovery copy in your browser — reopening the app offers it back.'
      ]
    };
  }

  const type = error && error.type;

  if (type === OpenPresentationErrorType.INVALID_JSON || isSyntaxError(error)) {
    const location = details.location || null;
    return {
      ...base,
      kind: LoadErrorKind.INVALID_JSON,
      title: 'That is not valid JSON',
      summary: describeJsonBreak(subject, location),
      location,
      detail: details.reason || safeMessage(error),
      suggestions: [
        'A truncated file is the usual cause — check the very end of it for a missing } or ].',
        'Paste it into a JSON validator to see the exact character that broke it.',
        ...(source === 'paste'
          ? ['Copy the whole reply, including the closing brace, and paste it again.']
          : ['If an assistant wrote it, ask for the file again rather than repairing it by hand.'])
      ]
    };
  }

  if (type === OpenPresentationErrorType.INVALID_FORMAT) {
    const issues = Array.isArray(details.issues) ? details.issues : [];
    const keys = Array.isArray(details.keys) ? details.keys : [];
    return {
      ...base,
      kind: LoadErrorKind.INVALID_FORMAT,
      title: 'That is valid JSON, but not a lesson deck',
      summary: `${subject} parsed fine, but it does not have the shape this app reads — loading it would have given you an empty deck.`,
      issues,
      detail: keys.length
        ? `Top-level keys found: ${keys.join(', ')}`
        : safeMessage(error),
      suggestions: [
        'Decks saved by this app have a "sections" object and a "version" or "schema_version" field.',
        'A worksheet, a template settings file or an HTML export will not open here — use the matching import instead.',
        'If an assistant produced it, hand it the LLM template from the Export menu and ask again.'
      ]
    };
  }

  if (type === OpenPresentationErrorType.FILE_READ_ERROR) {
    return {
      ...base,
      kind: LoadErrorKind.UNREADABLE,
      title: 'That file could not be read',
      summary: 'The browser could not read the file off the disk, so nothing was loaded.',
      suggestions: [
        'Check the file still exists and has not been moved since you chose it.',
        'Copy it out of cloud storage to a local folder and open it from there.',
        'If it is on a network drive, make sure the drive is still connected.'
      ]
    };
  }

  if (type === LoadErrorKind.INCOMPLETE) {
    return {
      ...base,
      kind: LoadErrorKind.INCOMPLETE,
      title: 'This deck is missing required fields',
      summary: context.startedBlank
        // The editor behind the panel is showing its default deck, not theirs.
        ? `${subject} is a lesson deck, but parts this app needs are missing or damaged. The editor has opened an empty deck instead — nothing from your file is in it.`
        : `${subject} is a lesson deck, but parts this app needs are missing or damaged, so it was not loaded.`,
      issues: Array.isArray(details.issues) ? details.issues : [],
      suggestions: [
        'Open it in the version of the app that saved it and export it again.',
        'If it came from an assistant, regenerate it from the LLM template in the Export menu.',
        context.startedBlank
          ? 'Do not save over the original file — it still holds everything this one is missing.'
          : 'Your current deck is untouched — save it before experimenting further.'
      ]
    };
  }

  return base;
}

/**
 * Build the error object for a deck that parsed and validated but could not be
 * turned into editor state — the import pipeline's own refusals.
 *
 * @param {string[]} issues - Whatever the normalizer complained about
 * @param {{ fileName?: string, message?: string }} [context]
 */
export function incompleteDeckError(issues = [], context = {}) {
  const error = new Error(context.message || 'The deck is missing required fields');
  error.type = LoadErrorKind.INCOMPLETE;
  error.details = {
    issues: issues.map((issue) => (typeof issue === 'string' ? issue : String(issue?.message || issue))),
    fileName: context.fileName || ''
  };
  return error;
}

/**
 * Say where the JSON broke, as precisely as the browser allowed and no more.
 * An approximate location is announced as one — a confidently wrong column
 * sends people to the wrong line and wastes more time than saying nothing.
 */
function describeJsonBreak(subject, location) {
  if (!location) return `${subject} is not valid JSON, so none of it could be read.`;
  if (location.column) {
    return `${subject} breaks the JSON format at line ${location.line}, column ${location.column}. Nothing before that point was loaded.`;
  }
  return `${subject} breaks the JSON format around line ${location.line}. Nothing after that point was loaded.`;
}

function isSyntaxError(error) {
  return Boolean(error) && (error.name === 'SyntaxError' || error instanceof SyntaxError);
}

function safeMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return String(error.message || '');
}

export default describeLoadError;
